// dependencies
import axios from "axios";
import allSettled from "promise.allsettled";
import { fromJS } from "immutable";
import { GenerateProcess } from "redux-process";

// ui
import ui from "./ui";

// actions
import { updateUI } from "Actions/ui";
import { createDesktopNotification } from "Actions/notifications";

// selectors
import { getPageName, getUI } from "Selectors/ui";
import { getDeployments, getBranches as getLocalBranches, selectDeployment } from "./selectors";

// services
import { getBranches as getGithubBranches } from "../../Api/services/github";

let checkBranchesTimer = null;
let checkDeploymentInfoTimer = null;

export const changeUI = (keyPath, value) => (dispatch, getState) => {
	const state = getState();
	const pageName = getPageName(state);
	const newKeyPath = [pageName].concat(keyPath);

	dispatch(updateUI(newKeyPath, value));
};

export const getBranches = () => async (dispatch, getState) => {
	try {
		const branches = await getGithubBranches();

		const nextUI = getUI(getState()).withMutations((mutable) => {
			mutable.setIn(["branches"], fromJS(branches));
		});

		dispatch(changeUI([], nextUI));
	} catch (error) {
		console.error("Error fetching branches:", error);
	}
};

export const updateDeployment = (domain, nextCommitHash, deploymentType) => async (dispatch) => {
	const response = await dispatch(
		GenerateProcess("updateDeployment", { payload: { domain, nextCommitHash, deploymentType } })
	);

	if (response.status === 200) {
		dispatch({
			type: "[100] create info toast",
			message: `Tearing down deployment on ${domain}`,
			actionMessage: "Dismiss",
		});
	}
};

/**
 * Pings the deployment to receive the following information:
 *  - Git Branch
 *  - Git Hash
 *  - Is it healthy?
 *  - Is there a newer version of the branch?
 * @param {String} domain - domain of the deployment
 */
export const getDeploymentInfo = (domain) => async (dispatch, getState) => {
	try {
		let healthy = false;
		let nextUpdateIsReady = false;

		let gitHash = "";
		let gitBranch = "";
		let response = {};

		try {
			response = await axios.get(`https://${domain}.staging.talent.performyard.com/health`);
			gitHash = response.headers["x-git-hash"];
			gitBranch = response.headers["x-git-branch"];
		} catch (ermahgerd) {
			gitHash = "";
			gitBranch = "";
		}

		const state = getState();
		const branches = getLocalBranches(state);
		const matchedBranch = branches.find((item) => item.branchName === gitBranch);

		try {
			const [healthCheck, newBuildHealthCheck] = await allSettled([
				axios.get(
					`https://cdn.performyard.com/${gitHash}/gen/production/COMMITHASH?bust=${new Date().getTime()}`
				),
				matchedBranch && matchedBranch.commitHash !== gitHash
					? axios.get(
							`https://cdn.performyard.com/${
								matchedBranch.commitHash
							}/gen/production/COMMITHASH?bust=${new Date().getTime()}`
						)
					: null,
			]);

			healthy = healthCheck.value.status === 200;
			nextUpdateIsReady = !!(newBuildHealthCheck.value && newBuildHealthCheck.value.status === 200);
		} catch (ermahgerd) {
			healthy = false;
			nextUpdateIsReady = false;
		}

		const domainData = {
			gitHash,
			healthy,
			gitBranch,
			nextUpdateIsReady,
		};

		const currentDeploymentState = selectDeployment(getState(), domain);

		try {
			// if a domain goes from unhealthy to healthy
			if (currentDeploymentState.get("healthy") === false && domainData.healthy === true) {
				const notification = new Notification("Logan", {
					icon: "/logo.png",
					body: `${domain} is now healthy.`,
				});

				// close after 15 seconds
				setTimeout(notification.close.bind(notification), 15 * 1000);
			} else if (
				currentDeploymentState.get("nextUpdateIsReady") === false &&
				domainData.nextUpdateIsReady === true
			) {
				const notification = new Notification("Logan", {
					icon: "/logo.png",
					body: `${domain} has an update. Click here to update.`,
				});

				// close after 15 seconds
				setTimeout(notification.close.bind(notification), 15 * 1000);
			}
		} catch (err) {
			// pass
			console.log(err);
		}

		dispatch({ type: "DEPLOYMENTS:UPDATEDOMAIN", domain, domainData });
	} catch (ermahgerd) {
		dispatch({ type: "DEPLOYMENTS:UPDATEDOMAIN", domain, domainData: {} });
	}
};

export const deployBranch = (commitHash, gitBranch, deploymentType) => async (dispatch) => {
	const response = await dispatch(
		GenerateProcess("deployBranch", { payload: { commitHash, gitBranch, deploymentType } })
	);

	if (response.status === 200) {
		dispatch({ type: "[100] create info toast", message: "Deploying...", actionMessage: "Dismiss" });
	}
};

export const checkAllDeploymentInfo = () => (dispatch, getState) => {
	const state = getState();
	const deployments = getDeployments(state);

	deployments.forEach((deployment) => {
		dispatch(getDeploymentInfo(deployment.get("domain")));
	});
};

export const fetchDeployments = () => async (dispatch) => {
	await dispatch(GenerateProcess("getDeployments"));
};

export const teardownDeployment = (domain) => async (dispatch) => {
	const response = await dispatch(GenerateProcess("teardownDeployment", { payload: { domain } }));

	if (response.status === 200) {
		dispatch({ type: "[100] create info toast", message: "Tearing down deployment", actionMessage: "Dismiss" });
	}
};

export const init = () => async (dispatch) => {
	// init ui
	dispatch(changeUI([], ui));

	// request access to desktop notifications
	if (Notification.permission !== "denied") {
		Notification.requestPermission();
	}

	// do some initial requests
	await Promise.all([dispatch(getBranches()), dispatch(fetchDeployments())]);

	// get deployment info
	dispatch(checkAllDeploymentInfo());

	// do everything above on a timer
	checkBranchesTimer = setInterval(() => {
		dispatch(getBranches());
	}, 30000);

	checkDeploymentInfoTimer = setInterval(async () => {
		await dispatch(fetchDeployments());
		dispatch(checkAllDeploymentInfo());
	}, 5000);
};

export const teardownComponent = () => () => {
	clearTimeout(checkBranchesTimer);
	clearTimeout(checkDeploymentInfoTimer);
};
